import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import type { NewsItem } from '@/types';
import EmptyState from '@/components/base/EmptyState';
import ShareDialog from '@/components/base/ShareDialog';
import { useState } from 'react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const item = (newsList as NewsItem[]).find((news) => news.id === id);
  const [shareOpen, setShareOpen] = useState(false);

  const related = (newsList as NewsItem[])
    .filter((news) => news.id !== id)
    .sort((a, b) => {
      const aScore = a.category === item?.category ? 1 : 0;
      const bScore = b.category === item?.category ? 1 : 0;
      if (aScore !== bScore) return bScore - aScore;
      return a.date < b.date ? 1 : -1;
    })
    .slice(0, 3);

  useDocumentMeta({
    title: item ? `${item.title} | 善良的女秘书影视资讯` : '影视资讯 | 善良的女秘书',
    description: item ? item.summary.slice(0, 120) : '善良的女秘书影视资讯详情。',
    keywords: item ? `${item.category},${item.title},影视资讯,善良的女秘书` : '影视资讯',
    type: 'article',
  });

  if (!item) {
    return (
      <div className="w-full px-4 py-20 md:px-8 lg:px-12">
        <EmptyState
          title="未找到该资讯"
          description="这篇资讯可能已下线，去看看其他最新内容吧。"
          actionLabel="返回资讯列表"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/news');
          }}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 lg:px-12">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="whitespace-nowrap transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <Link to="/news" className="whitespace-nowrap transition-colors hover:text-primary-600">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="line-clamp-1 text-foreground-800">资讯详情</span>
      </nav>

      <article className="mx-auto max-w-3xl">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-accent-100 px-2 py-0.5 text-accent-900">
            {item.category}
          </span>
          <span className="text-foreground-400">{item.date}</span>
        </div>

        <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
          {item.title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {item.views.toLocaleString()} 阅读
          </span>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="ml-auto flex cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-share-line"></i>
            分享
          </button>
        </div>

        <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 资讯配图`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-6 space-y-5">
          {item.content.map((paragraph, index) => (
            <p
              key={index}
              className="text-[15px] leading-loose text-foreground-700"
            >
              {paragraph}
            </p>
          ))}
        </div>

        <p className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-3 text-xs text-foreground-400">
          本站内容均为影视资讯展示与学习交流用途，不代表任何官方立场，也不提供在线播放或下载服务。
        </p>
      </article>

      <section className="mx-auto mt-14 max-w-3xl">
        <h2 className="mb-5 font-heading text-xl font-semibold text-foreground-950">
          相关资讯推荐
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {related.map((news) => (
            <Link
              key={news.id}
              to={`/news/${news.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="h-32 w-full overflow-hidden bg-background-200">
                <img
                  src={news.cover}
                  alt={news.title}
                  title={`${news.title} 资讯详情`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-3.5">
                <span className="text-xs text-foreground-400">{news.date}</span>
                <h3 className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                  {news.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />
    </div>
  );
}